import { Box, Skeleton, Stack, useTheme } from "@mui/material";
import { HTMLBox } from "src/components/HTMLBox/HTMLBox";

export type ActionOverviewVariant = "catalog" | "engaged";

type ActionOverviewCardProps = {
    description?: string;
    imageUrl?: string | null;
    variant: ActionOverviewVariant;
};

export const ActionOverviewCard = (props: ActionOverviewCardProps) => {
    const { description, imageUrl, variant } = props;
    const theme = useTheme();
    const hasDescription = description?.trim() !== "" && description !== undefined;

    if (!imageUrl && !hasDescription) {
        return null;
    }

    const desktopImageHeight = variant === "catalog" ? theme.spacing(18.75) : theme.spacing(15);

    return (
        <Stack
            direction={{ xs: "column", md: "row" }}
            gap={theme.spacing(3)}
            padding={theme.spacing(3)}
            boxShadow={theme.custom.shadows.grey}
            border={`1px solid ${theme.custom.grey[600]}`}
            borderRadius={theme.spacing(1.5)}
            bgcolor={theme.palette.common.white}
            alignItems={{ xs: "stretch", md: "flex-start" }}
        >
            {imageUrl && (
                <Box
                    component="img"
                    src={imageUrl}
                    alt=""
                    sx={{
                        aspectRatio: { xs: "5 / 3", md: "auto" },
                        borderRadius: theme.spacing(1.5),
                        flexShrink: 0,
                        height: { xs: "auto", md: desktopImageHeight },
                        objectFit: "cover",
                        width: { xs: "100%", md: theme.spacing(31.25) },
                    }}
                />
            )}
            {hasDescription && <HTMLBox value={description} />}
        </Stack>
    );
};

type ActionOverviewSkeletonProps = {
    variant: ActionOverviewVariant;
};

export const ActionOverviewSkeleton = (props: ActionOverviewSkeletonProps) => {
    const { variant } = props;
    const theme = useTheme();
    const desktopImageHeight = variant === "catalog" ? theme.spacing(18.75) : theme.spacing(15);

    return (
        <Stack
            aria-busy="true"
            direction={{ xs: "column", md: "row" }}
            gap={theme.spacing(3)}
            padding={theme.spacing(3)}
            border={`1px solid ${theme.custom.grey[600]}`}
            borderRadius={theme.spacing(1.5)}
            bgcolor={theme.palette.common.white}
        >
            <Skeleton
                animation="wave"
                variant="rounded"
                sx={{
                    aspectRatio: { xs: "5 / 3", md: "auto" },
                    flexShrink: 0,
                    height: { xs: "auto", md: desktopImageHeight },
                    width: { xs: "100%", md: theme.spacing(31.25) },
                }}
            />
            <Stack flex={1} gap={theme.spacing(1)}>
                <Skeleton animation="wave" variant="text" width="100%" />
                <Skeleton animation="wave" variant="text" width="100%" />
                <Skeleton animation="wave" variant="text" width="75%" />
            </Stack>
        </Stack>
    );
};
